import { Text, MediaQuery, useMediaQuery, Link } from "@certn/cascadia-react";
import { formatDateTime, slugify } from "@certn/utils";
import { css } from "@emotion/css";
import facepaint from "facepaint";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface ApplicantDetailsProps {
  firstName: string | null;
  lastName: string | undefined;
  middleName: string | undefined;
  dateOfBirth: string | null;
  email: string | null;
  currentLocation: {
    street_address: string;
    unit_number?: string | undefined;
    locality: string;
    administrative_division_1?: string | undefined;
    country: string;
    postal_code: string;
  };
  isLoading?: boolean;
}
function formatAddress(currentLocation: any) {
  const formattedAddressParts = [
    currentLocation?.unit_number && `${currentLocation.unit_number}-`,
    currentLocation?.street_address,
    currentLocation?.locality && `, ${currentLocation.locality}`,
    currentLocation?.administrative_division_1 &&
      `, ${currentLocation.administrative_division_1}`,
    currentLocation?.postal_code && ` ${currentLocation.postal_code}`,
    currentLocation?.country && ` ${currentLocation.country}`,
  ];

  return formattedAddressParts.filter(Boolean).join("");
}

function showAllFields(
  fields: { title: string; value: string | null | undefined }[],
  mediaQuery: facepaint.DynamicStyleFunction,
  isMobile?: boolean,
  isLoading?: boolean
) {
  return (
    <div
      className={css(
        styles.gridContainer,
        mediaQuery({
          display: ["block", "block", "grid"],
        })
      )}
    >
      {fields.map(
        (field: { title: string; value: string | null | undefined }, i) => {
          return (
            <div
              key={i}
              className={css(
                styles.applicantDetails,
                mediaQuery({
                  marginBottom: ["var(--space-3)", "var(--space-3)", 0],
                })
              )}
            >
              <Text style="summary/content-secondary">{field.title}</Text>
              <Text
                className={styles.gridItem}
                style="summary/content-primary"
                testID={
                  isMobile
                    ? "sm-applicant-details-" + slugify(field.title) + "-value"
                    : "lg-applicant-details-" + slugify(field.title) + "-value"
                }
                isLoading={isLoading}
              >
                {field.value || "--"}
              </Text>
            </div>
          );
        }
      )}
    </div>
  );
}

export function ApplicantDetails({
  firstName,
  lastName,
  middleName,
  dateOfBirth,
  email,
  currentLocation,
  isLoading,
}: ApplicantDetailsProps) {
  const { t } = useTranslation();
  const { mediaQuery } = useMediaQuery();
  const [showMore, setShowMore] = useState(false);
  const { enableDobRedactionAcrossApps } = useFlags();

  const fields = [
    { title: t("Given name"), value: firstName },
    { title: t("Additional given name"), value: middleName },
    { title: t("Family name"), value: lastName },
    {
      title: t("Date of birth"),
      value: enableDobRedactionAcrossApps
        ? dateOfBirth
        : formatDateTime(dateOfBirth, "MMMM dd, yyyy", "--"),
    },

    { title: t("Email"), value: email },
    { title: t("Address"), value: formatAddress(currentLocation) },
  ];

  return (
    <div style={{ marginTop: "var(--space-4)" }}>
      <div className={styles.header}>
        <Text style="global/heading-medium">{t("Profile details")}</Text>
      </div>

      <MediaQuery sm md type="show">
        {!showMore ? (
          <>
            <div
              className={css(
                styles.applicantDetails,
                mediaQuery({
                  marginBottom: ["var(--space-3)", "var(--space-3)", 0],
                })
              )}
            >
              <Text style="summary/content-secondary">{t("Given name")}</Text>
              <Text
                style="summary/content-primary"
                testID="sm-applicant-details-first-name-value"
                isLoading={isLoading}
              >
                {firstName || "--"}
              </Text>
            </div>
            <div
              className={css(
                styles.applicantDetails,
                mediaQuery({
                  marginBottom: ["var(--space-3)", "var(--space-3)", 0],
                })
              )}
            >
              <Text style="summary/content-secondary">{t("Family name")}</Text>
              <Text
                style="summary/content-primary"
                testID="sm-applicant-details-last-name-value"
                isLoading={isLoading}
              >
                {lastName || "--"}
              </Text>
            </div>
          </>
        ) : (
          showAllFields(fields, mediaQuery, true, isLoading)
        )}
      </MediaQuery>
      <MediaQuery sm md type="hide">
        {showAllFields(fields, mediaQuery, undefined, isLoading)}
      </MediaQuery>

      <MediaQuery sm md type="show">
        <Link
          onPress={() => {
            setShowMore(!showMore);
          }}
          textColor="text-weak"
          testID="sm-applicant-details-show-more"
        >
          {!showMore ? t("Show more") : t("Show less")}
        </Link>
      </MediaQuery>
    </div>
  );
}

const styles = {
  gridContainer: css({
    gridTemplateColumns: "1fr 1fr 1fr",
    justifyContent: "space-between",
    alignItems: "start",
    columnGap: "var(--space-3)",
    rowGap: "var(--space-3)",
  }),
  applicantDetails: css({
    display: "grid",
  }),
  header: css({
    marginBottom: "var(--space-3)",
  }),
  gridItem: css({
    wordWrap: "break-word",
    wordBreak: "break-word",
  }),
};
